import { Button } from '@sede-enterprise/shell-ds-react-framework';
import {
  POWER,
  GRID,
  GAS_FIRED_GENERATION,
  TECH_SOURCE_COLUMN,
  TECH_SOURCE_COLUMN_POWER,
  PRODUCTS_COLUMN,
} from '../../api/constants';
import React, { useContext } from 'react';
import { AppContext } from '../../context/calculatorFormContext';
import { IManualFormData, type Idispatch } from '../../types/types';

export const ManualFormButtons = (props: {
  country: string;
  myState: IManualFormData;
  myDispatch: Idispatch;
}) => {
  const { state, dispatch } = useContext(AppContext);
  const validateForm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { country, myState, myDispatch } = props;
    const {
      opportunity,
      evp,
      business,
      region,
      commodity,
      fuelType,
      assetType,
      operatingModel,
      equityShare,
      efficiency,
      gridLocation,
      isGoToStepTwoClicked,
      countryOptions,
      regionOptions,
    } = state;

    const { techSource, startDate, endDate, grossMarginVal, uomVal } = myState;

    const isValidOpportunity = opportunity !== '';
    const isValidEvp = evp !== '';
    const isValidBusiness = business !== '';
    // Only validate region if the field is enabled (has options available)
    const isValidRegion = regionOptions.length > 1 ? region !== '' : true;
    // Only validate country if the field is enabled (has options available)
    const isValidCountry = countryOptions.length > 1 ? country !== '' : true;
    const isValidCommodity = commodity !== '';
    const isValidStartDate = startDate !== '';
    const isValidEndDate = endDate !== '';

    // Helper function to normalize EVP name for comparison
    const normalizeEvpName = (evpName: string): string =>
      evpName.toLowerCase().replace(/[^a-z0-9]/g, '');

    // Gross margin is not required for SLMT
    const isValidGrossMargin =
      normalizeEvpName(state.evp) === 'slmt' ? true : grossMarginVal !== '';

    // Validate Carbon Credit field only for T&S Products
    const isValidCarbonCredit =
      normalizeEvpName(state.evp) === 'tsproducts' ? state.carbonCredit !== -1 : true;

    myDispatch({ type: 'setisOpportunityValid', payload: isValidOpportunity });
    myDispatch({ type: 'setisEvpValid', payload: isValidEvp });
    myDispatch({ type: 'setisBusinessValid', payload: isValidBusiness });
    myDispatch({ type: 'setisRegionValid', payload: isValidRegion });
    myDispatch({ type: 'setisCountryValid', payload: isValidCountry });
    myDispatch({ type: 'setisCommodityValid', payload: isValidCommodity });
    myDispatch({ type: 'setisStartDateValid', payload: isValidStartDate });
    myDispatch({ type: 'setisEndDateValid', payload: isValidEndDate });
    myDispatch({ type: 'setisGrossMarginValid', payload: isValidGrossMargin });
    myDispatch({ type: 'setisCarbonCreditValid', payload: isValidCarbonCredit });

    const isCommodityPowerAndEmptyTechSources = commodity === POWER && techSource.length === 0;
    const isValidTechSource = !isCommodityPowerAndEmptyTechSources;
    myDispatch({ type: 'setisTechSourceValid', payload: isValidTechSource });

    const isValidGridLocation = techSource.includes(GRID) ? (gridLocation ?? '') !== '' : true;
    myDispatch({ type: 'setIsGridLocationValid', payload: isValidGridLocation });

    let isValidFuelType = true;
    let isValidAssetType = true;
    let isValidOperatingModel = true;
    let isValidEquityShare = true;
    let isValidEfficiency = true;

    // if Gas Fired Generation is opted as Tech Source
    const isGasFiredGeneration = techSource.includes(GAS_FIRED_GENERATION);

    if (isGasFiredGeneration) {
      isValidFuelType = fuelType !== '';
      isValidAssetType = assetType !== '';
      isValidOperatingModel = operatingModel !== '';
      isValidEquityShare = equityShare !== '';
      isValidEfficiency = efficiency !== '';
      myDispatch({ type: 'setIsFuelTypeValid', payload: isValidFuelType });
      myDispatch({ type: 'setIsAssetTypeValid', payload: isValidAssetType });
      myDispatch({ type: 'setIsOperatingModelValid', payload: isValidOperatingModel });
      myDispatch({ type: 'setIsEquityPercentageValid', payload: isValidEquityShare });
      myDispatch({ type: 'setIsEfficiencyValid', payload: isValidEfficiency });
    }

    const isFormValid = [
      isValidTechSource,
      isValidCommodity,
      isValidEvp,
      isValidBusiness,
      isValidRegion,
      isValidCountry,
      isValidGrossMargin,
      isValidOpportunity,
      isValidStartDate,
      isValidEndDate,
      isValidFuelType,
      isValidOperatingModel,
      isValidEquityShare,
      isValidEfficiency,
      isValidGridLocation,
      isValidAssetType,
      isValidCarbonCredit,
    ].every((isValid) => isValid);

    myDispatch({ type: 'setIsWarningMsgAvailable', payload: !isFormValid });
    dispatch({ type: 'setIsFormValidated', payload: isFormValid });

    processForm(
      isFormValid,
      techSource,
      fuelType,
      evp,
      commodity,
      uomVal,
      isGoToStepTwoClicked,
      dispatch,
    );
  };

  const processForm = (
    isFormValid: boolean,
    techSource: string[],
    fuelType: string,
    evp: string,
    commodity: string | string[],
    uomVal: string,
    isGoToStepTwoClicked: boolean,
    dispatch: any,
  ) => {
    if (isFormValid) {
      const techSourceSelected: string[] = [...techSource];
      if (techSourceSelected.includes(GAS_FIRED_GENERATION as string)) {
        techSourceSelected.splice(techSourceSelected.indexOf(GAS_FIRED_GENERATION), 1);
        techSourceSelected.push(fuelType);
      }

      // Determine the correct column header based on EVP type
      const isTSProducts = evp?.toLowerCase().replace(/[^a-z0-9]/g, '') === 'tsproducts';
      const columnPrefix = isTSProducts
        ? PRODUCTS_COLUMN
        : commodity === POWER
          ? TECH_SOURCE_COLUMN_POWER
          : TECH_SOURCE_COLUMN;
      const firstColName = columnPrefix + uomVal;

      dispatch({ type: 'setTechnologySource', payload: techSourceSelected });
      dispatch({ type: 'setStepTwoTableFirstCol', payload: firstColName });
      dispatch({ type: 'setIsGoToStepTwoClicked', payload: !isGoToStepTwoClicked });
      dispatch({ type: 'setActiveKey', payload: [0, 1] });
    }
  };

  const reload = () => document.location.reload();
  return (
    <div>
      <Button className='btn-size margin-right' variant='outlined' onClick={reload}>
        Reset Form
      </Button>
      <Button
        variant='filled'
        className='btn-size'
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          validateForm(e);
        }}
      >
        Go to step 2
      </Button>
    </div>
  );
};
